import * as React from 'react';
import {
  AlignItems,
  Body1,
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  IconVariant,
  Join,
  Space,
  Strong,
} from '@gousto-internal/citrus-react';
import Image from 'next/image';

import { FullWidthGrid } from '../layout/FullWidthGrid';
import { FeaturesColumn } from '../layout/FeaturesColumn';
import { SectionHeading } from '../SectionHeading';
import { FeatureList } from '../FeatureList';

import PrimaryImage from '../../../public/assets/homepage/meals-that-dont-cost-section/meals-that-dont-cost-primary.jpg';
import BCorpsLogo from '../../../public/assets/bcorps-logo.svg';
import { GetStartedCTA } from '../GetStartedCTA';

import { useImageSizes } from '../../../hooks/useImageSizes';

export function MealsThatDontCostTheEarthSection({
  ctaHref,
  mobileHiddenCta,
  ctaOnClick,
  priceUpdateEnabled = false,
}: {
  ctaHref?: string | null;
  mobileHiddenCta?: boolean;
  ctaOnClick?: () => void;
  priceUpdateEnabled?: boolean;
} = {}) {
  const primaryImageSizes = useImageSizes(['100vw', null, '50vw']);

  return (
    <section aria-labelledby="meals-that-dont-cost-heading">
      <FullWidthGrid>
        <FeaturesColumn bg={Color.NeutralGrey_50}>
          <Join with={<Space size={[4, null, 6]} />}>
            <SectionHeading color={Color.ColdGrey_800} id="meals-that-dont-cost-heading">
              {`Meals that don't cost the earth`}
            </SectionHeading>

            <FeatureList name="meals-that-dont-cost-features" iconVariant={IconVariant.Default}>
              {priceUpdateEnabled ? (
                <Body1>
                  Get more bang for your box with meals from just <s>€3.99</s> €2.00 on your first
                  order <sup>§</sup>
                </Body1>
              ) : (
                <Body1>
                  Ireland&apos;s best value recipe box, making quality meals more affordable.*
                </Body1>
              )}

              <Body1>
                All of our seafood is sustainably sourced through the Global Sustainable Seafood
                Initiative (GSSI) and is recognised through certification schemes such as MSC & ASC.
              </Body1>

              <Body1>
                Each Gousto box <Strong>cuts your carbon footprint by 23%</Strong> compared to the
                same shop at a supermarket<sup>†</sup>.
              </Body1>
            </FeatureList>

            <GetStartedCTA href={ctaHref} mobileHidden={mobileHiddenCta} onClick={ctaOnClick} />

            <Box
              position={DisplayPosition.Relative}
              display={Display.Flex}
              gap={[2, null, 4]}
              alignItems={AlignItems.Center}
            >
              <Image
                src={BCorpsLogo.src}
                alt="BCorps"
                quality={100}
                height={BCorpsLogo.height}
                width={BCorpsLogo.width}
                style={{ width: '70px', height: 'auto' }}
              />

              <Body2 color={Color.ColdGrey_800}>
                <Strong>We’re proud to be a B Corp</Strong>
              </Body2>
            </Box>
          </Join>
        </FeaturesColumn>

        <Box position={DisplayPosition.Relative}>
          <Image
            src={PrimaryImage.src}
            fill
            alt=""
            style={{ objectFit: 'cover' }}
            sizes={primaryImageSizes}
          />
        </Box>
      </FullWidthGrid>
    </section>
  );
}
